import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, DatabaseBackup, Download, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";

export default function BackupPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/gestionale/backup/status")
    .then((response) => setStatus(response.data))
    .catch(() => setStatus(null));

  useEffect(() => { loadStatus(); }, []);

  const exportBackup = async () => {
    setBusy(true);
    try {
      const response = await api.post("/gestionale/backup/export", null, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `CantierePro_Backup_${new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-")}.cprobackup`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Backup creato e scaricato");
      loadStatus();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Errore creazione backup");
    } finally {
      setBusy(false);
    }
  };

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("Il ripristino sostituirà i dati attuali. Prima verrà creato automaticamente un backup di sicurezza. Continuare?")) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/gestionale/backup/import", formData, {
        params: { scope: "full" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Backup del ${new Date(response.data.backup_date).toLocaleString("it-IT")} ripristinato`);
      loadStatus();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Ripristino non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <button onClick={() => navigate("/gestionale")} className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> Gestionale
      </button>
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Gestionale</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mt-2">Backup e ripristino</h1>
        <p className="text-muted-foreground mt-2">Protezione completa dei dati di CantierePro, Magazzino e relativi allegati.</p>
      </div>
      <section className="max-w-5xl rounded-xl border border-border bg-card p-6">
        <div className="flex items-start gap-3 mb-5">
          <DatabaseBackup className="h-6 w-6 text-primary mt-0.5" />
          <div>
            <h2 className="font-heading text-2xl font-bold">Archivio completo</h2>
            <p className="text-sm text-muted-foreground">Le credenziali e i segreti del server non vengono esportati.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="rounded-lg border bg-muted/20 p-4">
            <p className="text-xs font-bold tracking-wider text-muted-foreground">ULTIMO EXPORT</p>
            <p className="font-semibold mt-2">{status?.last_export_at ? new Date(status.last_export_at).toLocaleString("it-IT") : "Nessun backup esportato"}</p>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <p className="text-xs font-bold tracking-wider text-muted-foreground">ULTIMO RIPRISTINO</p>
            <p className="font-semibold mt-2">{status?.last_import_at ? new Date(status.last_import_at).toLocaleString("it-IT") : "Nessun backup importato"}</p>
            {status?.imported_backup_date && <p className="text-sm text-muted-foreground mt-1">Data dei dati importati: {new Date(status.imported_backup_date).toLocaleString("it-IT")}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={exportBackup} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Crea ed esporta backup
          </Button>
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-4 h-10 font-semibold hover:bg-muted">
            <History className="h-4 w-4" /> Importa e ripristina
            <input type="file" accept=".cprobackup" className="hidden" disabled={busy} onChange={(event) => importBackup(event.target.files?.[0])} />
          </label>
        </div>
      </section>
    </AppShell>
  );
}
